<template>
    <div>
        <div class="mb-3 text-right">
            <hts-form-button
                v-if="group.isAdmin || permissions.includes('area_add')"
                color="main"
                icon-name="hi-plus-circle-icon"
                icon-position="right"
                type="submit"
                @click="showSlideOverAddArea = true">
                {{ $t('labels.addArea') }}
            </hts-form-button>
        </div>
        <div class="w-full flex">
            <div class="w-1/2">
                <hts-map
                    map-style="height:580px"
                    :node-position="nodePosition"
                    :node-list="nodePositionList"
                    :area-data="areaData"
                    :zoom="12" />
            </div>
            <div class="w-1/2">
                <hts-data-table
                    ref="dataTable"
                    :headers="areaHeader"
                    :server="server"
                    :pagination="true"
                    :get-context-menu-items="getContextMenuItems" />
            </div>
            <add-area
                :show="showSlideOverAddArea"
                :size-slide-over="30"
                @refresh-tables="refreshTables()"
                @close="showSlideOverAddArea = false" />
            <edit-area
                :area-is-selected-to-edit="areaIsSelectedToEdit"
                :show="showSlideOverEditArea"
                :size-slide-over="30"
                @refresh-tables="refreshTables()"
                @close="showSlideOverEditArea = false" />
            <hts-modal
                :show="showPopupDeleteArea"
                :title="$t('labels.deleteArea')"
                type="confirm"
                :content="$t('labels.confirm')"
                @hide="closePopupDeleteArea"
                @confirm="handleDeleteArea" />
        </div>
    </div>
</template>

<script setup lang="ts">
import { IArea } from './ignore/imports/model'
import areaPageTabs from '@/data/area/area.json'
import AddArea from '~/pages/trucker-[id]/area/ignore/add-area.vue'
import EditArea from '~/pages/trucker-[id]/area/ignore/edit-area.vue'

const store = $store()
const group = store.system.getGroup
const permissions = ref<Array<String>>([])
const route = useRoute()
const server = ref({})
const dataTable = ref()
const nodeList = ref([])
const nodePositionList = ref<Array<{ [key: string]: any }>>([])
const nodePosition = reactive({ lat: 10.7561542, lng: 106.6896784 })
const areaData = reactive({})
const showSlideOverAddArea = ref(false)
const showSlideOverEditArea = ref(false)
const showPopupDeleteArea = ref(false)
const areaId = ref(false)
const areaIsSelectedToDelete = reactive<IArea>({
  name: '',
  primaryNode: {},
  description: ''
})
const areaIsSelectedToEdit = reactive<IArea>({
  name: '',
  primaryNode: {},
  description: ''
})
onBeforeMount(() => {
  route.meta.pageTabs = areaPageTabs
  server.value = $url.area.getList
  permissions.value = group.permissions
  getAllNode()
})
const areaHeader = [
  {
    field: 'name',
    headerName: $t('labels.name'),
    sortable: true,
    onCellClicked: (params: any) => {
      if (!params) {
        return null
      }
      Object.assign(nodePosition, {
        lat: parseFloat(params.data.primaryNode.lat),
        lng: parseFloat(params.data.primaryNode.lng)
      })
      Object.assign(areaData, params.data)
    }
  },
  {
    field: 'primaryNode',
    headerName: $t('labels.primaryNode'),
    sortable: true,
    onCellClicked: (params: any) => {
      if (!params) {
        return null
      }
      Object.assign(nodePosition, {
        lat: parseFloat(params.data.primaryNode.lat),
        lng: parseFloat(params.data.primaryNode.lng)
      })
    },
    valueGetter: (params: any) => {
      if (!params) {
        return null
      }
      return params.data.primaryNode.name
    }
  },
  {
    field: 'description',
    headerName: $t('labels.description'),
    sortable: true,
    onCellClicked: (params: any) => {
      if (!params) {
        return null
      }
      Object.assign(nodePosition, {
        lat: parseFloat(params.data.primaryNode.lat),
        lng: parseFloat(params.data.primaryNode.lng)
      })
      Object.assign(areaData, params.data)
    }
  }
]
const getContextMenuItems = (params: any) => {
  if (!params.node?.data) {
    return null
  }
  const menuItem = [
    group.isAdmin || permissions.value.includes('area_edit')
      ? {
        name: $t('labels.edit'),
        action: () => {
          handleEditArea(params.node?.data || {})
        }
      }
      : null,
    group.isAdmin || permissions.value.includes('area_delete')
      ? {
        name: $t('labels.delete'),
        action: () => {
          showPopupDeleteArea.value = true
          areaId.value = params.node?.data._id
        }
      }
      : null
  ]
  return menuItem
}

const refreshTables = () => {
  if (dataTable.value) {
    dataTable.value.refresh()
    getAllNode()
  }
}

const getAllNode = () => {
  const api = $urlAdmin.node.getAll
  $api(api)
    .then((data) => {
      if (!data) {
        return true
      }
      const responseData: any = data.data.value
      if (!responseData) {
        return true
      }
      if (responseData.success) {
        nodeList.value = responseData.data
        nodeList.value.forEach((item: any) => {
          nodePositionList.value = [
            ...nodePositionList.value,
            {
              position: {
                lat: parseFloat(item.lat),
                lng: parseFloat(item.lng)
              },
              icon: checkTypeIcon(item.type)
            }
          ]
        })
      }
    })
    .catch((error: any) => error)
}

const checkTypeIcon = (type: string) => {
  if (!type) {
    return null
  }
  switch (type) {
  case 'PORT':
    return '/assets/port-icon.png'
  case 'DEPOT':
    return '/assets/depot-icon.png'
  case 'ICD':
    return '/assets/icd-icon.png'
  case 'WAREHOUSE':
    return '/assets/warehouse-icon.png'
  case 'HUB':
    return '/assets/hub-icon.png'
  case 'TOLL_STATION':
    return '/assets/toll-station-icon.png'
  default:
    break
  }
}

const handleEditArea = (node: Object) => {
  Object.assign(areaIsSelectedToEdit, node)
  showSlideOverEditArea.value = true
}
const handleDeleteArea = () => {
  const api = $url.area.delete
  $api(api, { body: { id: areaId.value } })
    .then((data) => {
      if (!data) {
        return true
      }
      const responseData: any = data.data.value
      if (!responseData) {
        return true
      }
      if (responseData.success) {
        refreshTables()
        process.client && $toast().success($t('labels.deleteSuccess'))
        closePopupDeleteArea()
      }
    })
    .catch((error: any) => error)
}
const closePopupDeleteArea = () => {
  showPopupDeleteArea.value = false
  Object.assign(areaIsSelectedToDelete, {})
}
</script>
